import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StepIndicator } from "@/components/StepIndicator";
import { Swatches } from "@/components/Swatches";
import { AddOnItem } from "@/components/AddOnItem";
import { BuildYourOwn } from "@/components/BuildYourOwn";
import { COLLECTIONS, SIZES, TIERS, ITEMS, PACKAGE_FOOTNOTE, PRICING_NOTE, PRIORITY_REMOVAL, REMOVAL_WINDOW, BYO, estimateTotal } from "@/data/collections";
import { useBooking } from "@/context/BookingContext";
import { toast } from "sonner";

const line = "var(--pp-border)";
const muted = "var(--pp-text-muted)";
const money = (n) => `$${n.toLocaleString()}`;

export default function Configure() {
  const nav = useNavigate();
  const { booking, update } = useBooking();
  const collection = COLLECTIONS.find((c) => c.id === booking.collectionId);
  const [slide, setSlide] = useState(0);
  const [itemsOpen, setItemsOpen] = useState(false);
  const addonCount = Object.values(booking.addons || {}).reduce((a, b) => a + b, 0);
  const isBYO = booking.collectionId === "build-your-own";

  useEffect(() => { if (!collection) nav("/", { replace: true }); }, [collection, nav]);
  useEffect(() => { if (collection && !isBYO && !booking.size) update({ size: "standard" }); }, [collection, isBYO, booking.size, update]);
  if (!collection) return null;

  const tiers = TIERS[collection.id] || {};
  const tier = booking.size ? tiers[booking.size] : null;
  const est = estimateTotal(collection.id, booking.size, booking.addons, booking.priorityRemoval, booking.extraPumpkins);
  const belowMin = isBYO && Boolean(booking.size) && est.subtotal < BYO.minimum;
  const prev = () => setSlide((s) => (s - 1 + collection.gallery.length) % collection.gallery.length);
  const next = () => setSlide((s) => (s + 1) % collection.gallery.length);

  const setItem = (id, v) => {
    const q = Math.max(0, Math.floor(Number.isFinite(v) ? v : 0));
    const addons = { ...booking.addons };
    if (q === 0) delete addons[id]; else addons[id] = q;
    update({ addons });
  };

  const handleContinue = () => {
    if (!booking.size) return toast.error("Please choose the scale of your porch to continue");
    if (isBYO && booking.palette.colors.length === 0) return toast.error("Please choose at least one pumpkin color");
    if (isBYO && booking.palette.balance === "custom" && !booking.palette.primary) return toast.error("Please choose your primary color");
    if (belowMin) return toast.error(`Build Your Own has a ${money(BYO.minimum)} minimum — you're at ${money(est.subtotal)}. Add a few pieces to continue.`);
    if (booking.priorityRemoval && !booking.removalDate) return toast.error("Please choose your preferred removal date");
    if (booking.priorityRemoval && (booking.removalDate < REMOVAL_WINDOW.start || booking.removalDate > REMOVAL_WINDOW.end)) return toast.error(`Removal dates run ${REMOVAL_WINDOW.label}`);
    nav("/schedule");
  };
  const canContinue = Boolean(booking.size) && (!isBYO || booking.palette.colors.length > 0) && !belowMin;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <StepIndicator current={2} />
      <section className="pp-container pb-20 pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6 lg:sticky lg:top-28 self-start">
            <div className="relative aspect-[4/5] sm:aspect-[5/6] overflow-hidden" data-testid="image-carousel">
              {collection.gallery.map((src, i) => (
                <img key={src} src={src} alt={`${collection.name} example ${i + 1}`} className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700" style={{ opacity: i === slide ? 1 : 0, objectPosition: (collection.ourWork || []).includes(i) ? "22% center" : "center" }} />
              ))}
              <button aria-label="Previous" data-testid="carousel-prev-button" onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 inline-flex items-center justify-center rounded-full" style={{ backgroundColor: "rgba(244,238,227,0.9)", color: "var(--pp-burgundy)" }}><ChevronLeft className="h-5 w-5" /></button>
              <button aria-label="Next" data-testid="carousel-next-button" onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 inline-flex items-center justify-center rounded-full" style={{ backgroundColor: "rgba(244,238,227,0.9)", color: "var(--pp-burgundy)" }}><ChevronRight className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 grid gap-3" style={{ gridTemplateColumns: `repeat(${collection.gallery.length}, minmax(0, 1fr))` }}>
              {collection.gallery.map((src, i) => (
                <button key={src} onClick={() => setSlide(i)} data-testid={`carousel-thumb-${i}`} className="aspect-[4/3] overflow-hidden transition-opacity" style={{ opacity: i === slide ? 1 : 0.45, outline: i === slide ? "2px solid var(--pp-terracotta)" : "none" }}>
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            {collection.inspiration && <p className="mt-3 text-[11px] italic" style={{ color: muted, opacity: 0.8 }} data-testid="inspiration-note">Inspiration imagery for the palette, alongside porches we have styled this season.</p>}
          </div>

          <div className="lg:col-span-6 space-y-10">
            <header>
              <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight" style={{ color: "var(--pp-burgundy)" }}>
                {isBYO ? "Build Your Own" : `The ${collection.name} Collection`}
              </h1>
              <p className="mt-4 text-lg" style={{ color: muted }} data-testid="running-estimate">
                {tier ? `${money(est.total)} estimate` : isBYO ? `Custom palettes from ${money(tiers.accent?.price || 0)}` : `Packages from ${money(tiers.accent?.price || 0)}`}
                <span className="text-sm"> · paid in full at checkout</span>
              </p>
              <div className="mt-6 h-px w-full" style={{ backgroundColor: line }} />
              <div className="mt-6 flex items-center gap-3 text-sm"><span className="font-medium" style={{ color: "var(--pp-burgundy)" }}>Palette:</span><Swatches colors={collection.colors} ring={line} /></div>
              <p className="mt-4 text-sm leading-relaxed" style={{ color: muted }}>{collection.description}</p>
            </header>

            {!isBYO && (
              <div data-testid="package-section">
                <h3 className="font-medium mb-4" style={{ color: "var(--pp-burgundy)" }}>Choose the scale of your porch</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-stretch">
                  {SIZES.map((s) => {
                    const active = booking.size === s.id;
                    const pop = s.popular;
                    return (
                      <button key={s.id} data-testid={`size-button-${s.id}`} onClick={() => update({ size: s.id })} className={`relative text-center px-2 transition-all duration-300 ${pop ? "py-7 sm:-my-2 shadow-[0_18px_40px_-24px_rgba(60,35,24,0.55)]" : "py-5"}`}
                        style={{ backgroundColor: active ? "var(--pp-terracotta)" : pop ? "var(--pp-parchment)" : "var(--pp-cream-card)", color: active ? "var(--pp-parchment)" : "var(--pp-burgundy)", border: `${pop ? 2 : 1}px solid ${active || pop ? "var(--pp-terracotta)" : line}` }}>
                        {pop && <span data-testid="most-popular-badge" className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] tracking-[0.18em] uppercase px-3 py-1" style={{ backgroundColor: "var(--pp-burgundy)", color: "var(--pp-parchment)" }}>Most popular</span>}
                        <div className="font-serif-display text-2xl">{s.label}</div>
                        <div className={`mt-1 ${pop ? "text-base font-medium" : "text-sm"}`} style={{ color: active ? "#F6E6D4" : pop ? "var(--pp-terracotta)" : muted }}>{money(tiers[s.id].price)}</div>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-xs italic" style={{ color: muted }} data-testid="pricing-note">{PRICING_NOTE}</p>
                {tier && (
                  <div className="mt-5 border p-5" style={{ borderColor: line, backgroundColor: "var(--pp-cream-card)" }} data-testid="tier-includes">
                    <div className="pp-caption mb-3">What's included · {SIZES.find((s) => s.id === booking.size).label} · {money(tier.price)}</div>
                    <ul className="space-y-2">
                      {tier.includes.map((t) => (
                        <li key={t} className="flex items-start gap-2 text-sm" style={{ color: "var(--pp-text)" }}><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "var(--pp-sage)" }} />{t}</li>
                      ))}
                    </ul>
                    <p className="mt-4 text-xs" style={{ color: muted }}>{PACKAGE_FOOTNOTE}</p>
                  </div>
                )}
              </div>
            )}

            {isBYO && <BuildYourOwn booking={booking} update={update} />}

            <div data-testid="items-section" className="border" style={{ borderColor: line, backgroundColor: "var(--pp-cream-card)" }}>
              <button type="button" data-testid="items-toggle" onClick={() => setItemsOpen((o) => !o)} aria-expanded={itemsOpen}
                className="w-full flex items-center justify-between gap-4 p-4 text-left">
                <div>
                  <h3 className="font-medium" style={{ color: "var(--pp-burgundy)" }}>Add-ons (optional)</h3>
                  <span className="block text-xs mt-0.5" style={{ color: muted }}>
                    {addonCount > 0 ? `${addonCount} item${addonCount === 1 ? "" : "s"} added · ${money(est.items)}` : "Extra pumpkins, mums, lanterns and more"}
                  </span>
                </div>
                <ChevronDown className="h-5 w-5 shrink-0 transition-transform" style={{ color: "var(--pp-terracotta)", transform: itemsOpen ? "rotate(180deg)" : "none" }} />
              </button>
              {itemsOpen && (
              <div className="px-4 pb-4" data-testid="items-panel">
              {["Pumpkins", "Plants", "Accents"].map((g) => (
                <div key={g} className="mt-3">
                  <div className="pp-caption mb-1">{g}</div>
                  <ul className="divide-y" style={{ borderColor: line }}>
                    {ITEMS.filter((i) => i.group === g).map((it) => (
                      <AddOnItem key={it.id} item={it} addons={booking.addons} onChange={setItem} />
                    ))}
                  </ul>
                </div>
              ))}
              </div>
              )}
            </div>

            <div className="border p-4" style={{ borderColor: line, backgroundColor: "var(--pp-cream-card)" }} data-testid="priority-removal">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid="priority-removal-toggle" checked={booking.priorityRemoval} onChange={(e) => update({ priorityRemoval: e.target.checked, removalDate: e.target.checked ? booking.removalDate : "" })} className="mt-1 h-4 w-4 accent-[var(--pp-terracotta)]" />
                <span><span className="font-medium" style={{ color: "var(--pp-burgundy)" }}>{PRIORITY_REMOVAL.title} — {money(PRIORITY_REMOVAL.price)}</span><span className="block text-sm mt-0.5" style={{ color: muted }}>{PRIORITY_REMOVAL.text}</span></span>
              </label>
              {booking.priorityRemoval && (
                <label className="block mt-4 pl-7" data-testid="removal-date-field">
                  <span className="pp-caption block mb-1">Your preferred removal date</span>
                  <input type="date" data-testid="removal-date-input" min={REMOVAL_WINDOW.start} max={REMOVAL_WINDOW.end} value={booking.removalDate}
                    onChange={(e) => update({ removalDate: e.target.value })}
                    className="bg-[var(--pp-cream)] px-3 py-2 text-sm outline-none border-b border-[var(--pp-border)] focus:border-[var(--pp-terracotta)]" />
                  <span className="block text-xs mt-1" style={{ color: muted }}>Any day {REMOVAL_WINDOW.label}. We'll do our best to hit your date and guarantee removal by December 1. We'll email you a reminder two days before.</span>
                </label>
              )}
            </div>

            {!isBYO && (
            <div data-testid="notes-section">
              <h3 className="font-medium mb-1" style={{ color: "var(--pp-burgundy)" }}>Anything we should know?</h3>
              <p className="text-xs mb-3" style={{ color: muted }}>Specific colors, gourds you love, where to place things — tell us here.</p>
              <textarea data-testid="customer-notes-input" rows={3} maxLength={1000} value={booking.customerNotes} onChange={(e) => update({ customerNotes: e.target.value })} placeholder="e.g. Please include a few warty gourds and keep the left side clear for the bench." className="w-full border p-3 text-sm outline-none focus:border-[var(--pp-terracotta)]" style={{ borderColor: line, backgroundColor: "var(--pp-cream-card)" }} />
            </div>
            )}

            <div className="border-t pt-5 text-sm" style={{ borderColor: line }} data-testid="estimate-breakdown">
              {tier && <Line k={`${SIZES.find((s) => s.id === booking.size).label} ${isBYO ? "custom palette" : "package"}`} v={money(est.tier)} />}
              {isBYO && est.extras > 0 && <Line k={`Additional pumpkins × ${booking.extraPumpkins}`} v={money(est.extras)} />}
              <Line k="À la carte items" v={money(est.items)} />
              {isBYO && <Line k="Custom palette design consultation" v={money(est.fee)} />}
              {booking.priorityRemoval && <Line k="Priority Removal" v={money(est.priority)} />}
              <div className="flex justify-between font-medium text-base mt-2" style={{ color: "var(--pp-burgundy)" }}><span>Estimated total</span><span data-testid="estimate-total">{money(est.total)}</span></div>
              {belowMin && <p className="mt-1 text-xs" style={{ color: "#8A2E2E" }} data-testid="byo-minimum-warning">Build Your Own orders have a {money(BYO.minimum)} minimum before the design consultation — you're at {money(est.subtotal)}.</p>}
            </div>

            <button data-testid="step-continue-button" onClick={handleContinue} disabled={!canContinue} className="pp-btn w-full sm:w-auto !px-14 !py-5 !text-base !tracking-[0.12em] !normal-case font-serif-display">
              Check available dates <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>
      <div className="flex-1" />
      <Footer />
    </div>
  );
}

const Line = ({ k, v }) => (<div className="flex justify-between py-1" style={{ color: "var(--pp-text-muted)" }}><span>{k}</span><span>{v}</span></div>);

